import { Battery } from "../../common/Battery";
import { getBatteryColors } from "../../../utils/battery";
import { gpsQuality } from "../../../utils/gpsStatus";
import { useT } from "../../../i18n";

// gpsQuality() 의 등급(Good/Poor/Unknown) → 표시 문구 키. 모르는 값은 원문 그대로.
const GPS_QUALITY_KEYS = {
  good: "mission.info.gps.good",
  poor: "mission.info.gps.poor",
  unknown: "mission.info.gps.unknown",
};

/**
 * 비행 상태 한 줄: 배터리 · GPS · 비행 시간. 그 아래 미션 진행 바.
 * 예전의 남은 시간·예상 종료 행과 상단 진행 링은 뺐다. 진행도는 이 바 하나로 본다.
 */
export default function DroneInfo({ drone, gpsStatus, progress, offline = false }) {
  const t = useT();
  // 오프라인(서버 하트비트 끊김)이면 마지막 값 대신 "-" 로 가린다. 정보 탭과 같은 기준.
  // 예전엔 마지막 배터리·GPS 가 그대로 남아 꺼진 드론이 살아 있는 것처럼 보였다.
  const rawPercent = drone?.battery?.percent;
  const hasBattery = !offline && rawPercent != null && Number.isFinite(Number(rawPercent));
  const batteryPercent = hasBattery
    ? Math.max(0, Math.min(100, Math.round(Number(rawPercent))))
    : null;
  const batteryColor = hasBattery ? getBatteryColors(batteryPercent).bar : undefined;
  const quality = offline ? null : gpsQuality(gpsStatus); // Good / Poor / Unknown / null
  const gpsClass = quality ? quality.toLowerCase() : "none";
  const qualityLabel = quality ? (GPS_QUALITY_KEYS[gpsClass] ? t(GPS_QUALITY_KEYS[gpsClass]) : quality) : "-";
  const gpsLevel = !offline && gpsStatus != null && Number.isInteger(Number(gpsStatus)) ? ` (${gpsStatus})` : "";
  const flightTime = offline ? "--:--:--" : drone?.timing?.flightTimeSec ?? "--:--:--";
  const done = Number(progress?.done) || 0;
  const total = Number(progress?.total) || 0;
  const pct = total > 0 ? Math.round((done * 100) / total) : 0;

  return (
    <section className="sb-card drone-info-card">
      <div className="calm-stats" aria-label={t("mission.info.stats")}>
        <div className="calm-stat">
          <span className="calm-stat-k">{t("mission.info.battery")}</span>
          <span className="calm-stat-v" style={batteryColor ? { color: batteryColor } : undefined}>
            {hasBattery ? `${batteryPercent}%` : "-"}
            {hasBattery && <Battery pct={batteryPercent} />}
          </span>
        </div>
        <div className="calm-stat">
          {/* 상태 점은 라벨 옆(모바일)과 값 뒤(데스크톱) 두 자리에 두고 CSS 로 하나만 보인다.
              모바일은 칸이 좁아 값 뒤에 점을 두면 오른쪽으로 몰려 보여서다. */}
          <span className={`calm-stat-k calm-gps calm-gps-k ${gpsClass}`}>
            {t("mission.info.gps")}
            <i aria-hidden="true" />
          </span>
          <span className={`calm-stat-v calm-gps ${gpsClass}`}>
            <i aria-hidden="true" />
            {qualityLabel}
            {gpsLevel}
          </span>
        </div>
        <div className="calm-stat">
          <span className="calm-stat-k">{t("mission.info.flightTime")}</span>
          <span className="calm-stat-v">{flightTime}</span>
        </div>
      </div>

      <div className="calm-progress" role="progressbar" aria-valuemin={0} aria-valuemax={100} aria-valuenow={pct}>
        <div className="calm-progress-head">
          {/* 개수는 라벨 옆, 퍼센트는 오른쪽 끝 */}
          <span className="calm-stat-k">
            {t("mission.info.progress")}
            <span className="calm-progress-count">{done} / {total}</span>
          </span>
          <span className="calm-progress-v">
            <em>{pct}%</em>
          </span>
        </div>
        <div className="calm-progress-bar">
          <i style={{ width: `${pct}%` }} />
        </div>
      </div>
    </section>
  );
}
